/**
 * Definiert die Struktur für Themes in der OdA Tools Plattform
 * Basierend auf dem UI-Styleguide
 */
export interface Theme {
  name: string;
  colors: {
    // Primärfarben
    primary: string;
    primaryLight: string;
    primaryDark: string;
    
    // Sekundärfarben
    secondary: string;
    secondaryDark: string;
    
    // Status-Farben
    success: string;
    danger: string;
    warning: string;
    info: string;
    
    // Hintergrundfarben
    background: string;
    backgroundLight: string;
    backgroundSecondary: string;
    backgroundTertiary: string;
    backgroundDark: string;
    surface: string;
    
    // Textfarben
    text: string;
    textLight: string;
    textDark: string;
    textOnPrimary: string;
    
    // UI-Elemente
    border: string;
    hover: string;
    focus: string;
    
    // Schatten
    shadowColor: string;
  };
  
  // Schatten-Definitionen
  shadows: {
    sm: string;
    md: string;
    lg: string;
  };
  
  // Weitere Theme-spezifische Eigenschaften
  properties: {
    headerHeight: string;
    sidebarWidth: string;
    borderRadius: {
      sm: string;
      md: string;
      lg: string;
    };
    transitions: {
      fast: string;
      normal: string;
      slow: string;
    };
    fonts?: {
      family: string;
      sizes: {
        h1: string;
        h2: string;
        h3: string;
        body: string;
        small: string;
      };
      weights: {
        regular: string;
        medium: string;
        semibold: string;
        bold: string;
      };
    };
    buttons?: {
      primary: {
        background: string;
        text: string;
        hover: string;
      };
      secondary: {
        background: string;
        text: string;
        hover: string;
      };
      neutral: {
        background: string;
        text: string;
        hover: string;
      };
      success: {
        background: string;
        text: string;
        hover: string;
      };
      warning: {
        background: string;
        text: string;
        hover: string;
      };
      danger: {
        background: string;
        text: string;
        hover: string;
      };
    };
    alerts?: {
      info: {
        background: string;
        text: string;
        icon: string;
      };
      success: {
        background: string;
        text: string;
        icon: string;
      };
      warning: {
        background: string;
        text: string;
        icon: string;
      };
      danger: {
        background: string;
        text: string;
        icon: string;
      };
    };
  };
}

/**
 * Standard-Light-Theme
 * Wird als Basis für andere Themes verwendet
 */
export const lightTheme: Theme = {
  name: 'light',
  colors: {
    // Primärfarben
    primary: '#0056b3',
    primaryLight: '#e6f0fa',
    primaryDark: '#004494',
    
    // Sekundärfarben
    secondary: '#6c757d',
    secondaryDark: '#5a6268',
    
    // Status-Farben
    success: '#28a745',
    danger: '#dc3545',
    warning: '#ffc107',
    info: '#17a2b8',
    
    // Hintergrundfarben
    background: '#ffffff',
    backgroundLight: '#f8f9fa',
    backgroundSecondary: '#e9ecef',
    backgroundTertiary: '#f1f3f4', // Heller für besseren Kontrast
    backgroundDark: '#e9ecef',
    surface: '#ffffff',
    
    // Textfarben
    text: '#212529',
    textLight: '#6c757d',
    textDark: '#343a40',
    textOnPrimary: '#ffffff',
    
    // UI-Elemente
    border: '#dee2e6',
    hover: '#f1f3f5',
    focus: 'rgba(0, 86, 179, 0.25)',
    
    // Schatten
    shadowColor: 'rgba(0, 0, 0, 0.1)',
  },
  
  // Schatten-Definitionen
  shadows: {
    sm: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
    md: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
    lg: '0 1rem 3rem rgba(0, 0, 0, 0.175)',
  },
  
  // Weitere Theme-spezifische Eigenschaften
  properties: {
    headerHeight: '60px',
    sidebarWidth: '250px',
    borderRadius: {
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
    },
    transitions: {
      fast: '150ms ease-in-out',
      normal: '300ms ease-in-out',
      slow: '500ms ease-in-out',
    },
    fonts: {
      family: '"Inter", "Roboto", "Helvetica Neue", sans-serif',
      sizes: {
        h1: '2rem', // 32px
        h2: '1.5rem', // 24px
        h3: '1.125rem', // 18px
        body: '1rem', // 16px
        small: '0.875rem', // 14px
      },
      weights: {
        regular: '400',
        medium: '500',
        semibold: '600',
        bold: '700'
      }
    },
    buttons: {
      primary: {
        background: '#0056b3',
        text: '#ffffff',
        hover: '#004494'
      },
      secondary: {
        background: '#6c757d',
        text: '#ffffff',
        hover: '#5a6268'
      },
      neutral: {
        background: '#f8f9fa',
        text: '#212529',
        hover: '#e9ecef'
      },
      success: {
        background: '#28a745',
        text: '#ffffff',
        hover: '#218838'
      },
      warning: {
        background: '#ffc107',
        text: '#212529',
        hover: '#e0a800'
      },
      danger: {
        background: '#dc3545',
        text: '#ffffff',
        hover: '#c82333'
      }
    },
    alerts: {
      info: {
        background: '#d1ecf1',
        text: '#0c5460',
        icon: '#17a2b8'
      },
      success: {
        background: '#d4edda',
        text: '#155724',
        icon: '#28a745'
      },
      warning: {
        background: '#fff3cd',
        text: '#856404',
        icon: '#ffc107'
      },
      danger: {
        background: '#f8d7da',
        text: '#721c24',
        icon: '#dc3545'
      }
    }
  },
}
